import { useCallback, useMemo, useState, Dispatch, SetStateAction } from "react";
import {
  PartConfirmationBucketEntry,
  SelectedSupplier,
} from "../utils/partsCalculationUtils";
import { mergePartConfirmationBucketEntries } from "../utils/partConfirmationBucketUtils";

export interface UsePartConfirmationBucketReturn {
  partConfirmationBucket: PartConfirmationBucketEntry[];
  /**
   * The bucket expressed as `SelectedSupplier[]` so it can be fed into
   * `calculateRemainingParts` alongside the Confirmed bucket.
   */
  partConfirmationBucketAsCommittedSuppliers: SelectedSupplier[];
  addSupplierToPartConfirmationBucket: (
    entry: PartConfirmationBucketEntry,
  ) => void;
  removeSupplierFromPartConfirmationBucket: (supplierId: string) => void;
  clearPartConfirmationBucket: () => void;
  isSupplierInPartConfirmationBucket: (supplierId: string) => boolean;
  setPartConfirmationBucket: Dispatch<
    SetStateAction<PartConfirmationBucketEntry[]>
  >;
}

/**
 * Ephemeral state for the Part Confirmation bucket in Supplier Discovery.
 * Cleared when the discovery modal closes.
 */
export const usePartConfirmationBucket =
  (): UsePartConfirmationBucketReturn => {
    const [partConfirmationBucket, setPartConfirmationBucket] = useState<
      PartConfirmationBucketEntry[]
    >([]);

    const addSupplierToPartConfirmationBucket = useCallback(
      (entry: PartConfirmationBucketEntry) => {
        setPartConfirmationBucket((prev) => {
          const existingIndex = prev.findIndex(
            (item) => item.supplierId === entry.supplierId,
          );
          if (existingIndex >= 0) {
            const next = [...prev];
            next[existingIndex] = mergePartConfirmationBucketEntries(
              prev[existingIndex],
              entry,
            );
            return next;
          }
          return [...prev, entry];
        });
      },
      [],
    );

    const removeSupplierFromPartConfirmationBucket = useCallback(
      (supplierId: string) => {
        setPartConfirmationBucket((prev) =>
          prev.filter((item) => item.supplierId !== supplierId),
        );
      },
      [],
    );

    const clearPartConfirmationBucket = useCallback(() => {
      setPartConfirmationBucket([]);
    }, []);

    const isSupplierInPartConfirmationBucket = useCallback(
      (supplierId: string) =>
        partConfirmationBucket.some((item) => item.supplierId === supplierId),
      [partConfirmationBucket],
    );

    const partConfirmationBucketAsCommittedSuppliers = useMemo<
      SelectedSupplier[]
    >(
      () =>
        partConfirmationBucket.map((entry) => ({
          supplierId: entry.supplierId,
          supplierName: entry.supplierName,
          selectedParts: entry.selectedParts,
          selectedAt: entry.addedAt,
        })),
      [partConfirmationBucket],
    );

    return {
      partConfirmationBucket,
      partConfirmationBucketAsCommittedSuppliers,
      addSupplierToPartConfirmationBucket,
      removeSupplierFromPartConfirmationBucket,
      clearPartConfirmationBucket,
      isSupplierInPartConfirmationBucket,
      setPartConfirmationBucket,
    };
  };
